import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Radio,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import NotFound from '@components/NotFound';

import { isProduction } from '@constants/env';

import { useApp } from '@hooks/useApp';

import ManageAccountLayout from '@views/ManageAccount/ManageAccountLayout';

const messages = defineMessages({
  title: {
    id: 'manageAccount.appearance',
    defaultMessage: 'Appearance',
  },
  subtitle: {
    id: 'manageAccount.appearance.subtitle',
    defaultMessage: 'Customize your visual experience',
  },
  themeLabel: {
    id: 'manageAccount.appearance.themeLabel',
    defaultMessage: 'Choose Theme',
  },
  light: {
    id: 'manageAccount.appearance.light',
    defaultMessage: 'Light Mode',
  },
  lightDesc: {
    id: 'manageAccount.appearance.lightDesc',
    defaultMessage: 'Bright and clear with high visibility.',
  },
  dark: {
    id: 'manageAccount.appearance.dark',
    defaultMessage: 'Dark Mode',
  },
  darkDesc: {
    id: 'manageAccount.appearance.darkDesc',
    defaultMessage: 'Dim and modern look, easy on the eyes.',
  },
  system: {
    id: 'manageAccount.appearance.system',
    defaultMessage: 'System',
  },
  systemDesc: {
    id: 'manageAccount.appearance.systemDesc',
    defaultMessage: 'Automatically match your device’s theme settings.',
  },
});

export default function ManageAppearancePage(props: PagePropsWithBreadcrumbs) {
  const { formatMessage } = useIntl();
  const {
    state: { themeMode },
    setThemeMode,
  } = useApp();
  const theme = useTheme();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const selected = event.target.value as 'light' | 'dark' | 'system';
    if (themeMode !== selected) setThemeMode(selected);
  };

  const options = [
    {
      value: 'light',
      label: formatMessage(messages.light),
      description: formatMessage(messages.lightDesc),
      icon: (
        <Icon
          color={theme.palette.warning.main}
          name="sun"
          size="lg"
          type="fas"
        />
      ),
    },
    {
      value: 'dark',
      label: formatMessage(messages.dark),
      description: formatMessage(messages.darkDesc),
      icon: (
        <Icon
          color={theme.palette.primary.main}
          name="moon"
          size="lg"
          type="fas"
        />
      ),
    },
    {
      value: 'system',
      label: formatMessage(messages.system),
      description: formatMessage(messages.systemDesc),
      icon: (
        <Icon
          color={theme.palette.info.main}
          name="gear"
          size="lg"
          type="fas"
        />
      ),
    },
  ];

  /** TEMP NOTE: Theme Selection temporarily disabled.
   * Appearance page returns NotFound, theme hardcoded to "light",
   * and route hidden until feature is finalized. */

  return isProduction ? (
    <NotFound />
  ) : (
    <ManageAccountLayout
      friendlyName={formatMessage(messages.title)}
      pageProps={props}
    >
      {() => (
        <Card
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[1],
          }}
        >
          <CardHeader
            avatar={
              <Icon
                color={theme.palette.primary.main}
                name="palette"
                size="lg"
                type="fas"
              />
            }
            title={
              <Typography variant="h6" fontWeight={600}>
                {formatMessage(messages.themeLabel)}
              </Typography>
            }
            subheader={
              <Typography variant="body2" color="text.secondary">
                {formatMessage(messages.subtitle)}
              </Typography>
            }
          />
          <Divider />

          <CardContent sx={{ p: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ justifyContent: 'center' }}
            >
              {options.map((opt) => {
                const selected = themeMode === opt.value;
                return (
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          color: theme.palette.background.paper,
                        },
                      },
                    }}
                    key={opt.value}
                    title={opt.description}
                    arrow
                  >
                    <Box
                      onClick={() =>
                        handleChange({
                          target: { value: opt.value },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                      sx={{
                        flex: 1,
                        p: 2,
                        borderRadius: 2,
                        textAlign: 'center',
                        border: selected
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.divider}`,
                        backgroundColor: selected
                          ? theme.palette.action.selected
                          : theme.palette.background.paper,
                        transition: 'all 0.25s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Stack alignItems="center" spacing={1}>
                        {opt.icon}
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          color="text.primary"
                        >
                          {opt.label}
                        </Typography>
                        <Radio
                          checked={selected}
                          value={opt.value}
                          onChange={handleChange}
                          color="primary"
                          sx={{ p: 0 }}
                        />
                      </Stack>
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}
    </ManageAccountLayout>
  );
}
